import type { PostCreateSchema } from '@repo/schemas/post/postCreate';
import type { PostEditSchema } from '@repo/schemas/post/postEdit';
import type { FilePublic } from '@repo/types/files';
import type { GroupPublic } from '@repo/types/groups';
import type { ChangeEvent } from 'react';

import { useFormContext } from 'react-hook-form';

import type { PostAudienceValue } from '@/hooks/posts';

import { FileInput, FileInputCard } from '@/components/ui/file-multiple-upload';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tiptap, TiptapToolbar } from '@/components/ui/tiptap-editor';
import { postExtensionSet } from '@/lib/tiptap-extensions-sets';

import { PostAuthorRow } from './PostAuthorRow';

type FormSchema = PostCreateSchema | PostEditSchema;

interface PostFormFieldsProps {
  files: { id: string; file: File }[];
  currentFiles?: FilePublic[];
  onRemoveFile: (id: string | number) => void;
  onAddFiles: (event: ChangeEvent<HTMLInputElement>) => void;
  author?: {
    name: string;
    surname: string;
    avatarURL?: string | null;
  };
  audience: PostAudienceValue;
  onOpenAudience: () => void;
  selectedGroups?: GroupPublic[];
}

export function PostFormFields(postFormFieldsProps: PostFormFieldsProps) {
  const { files, currentFiles = [], onRemoveFile, onAddFiles, author, audience, onOpenAudience, selectedGroups } = postFormFieldsProps;
  const { name = '', surname = '', avatarURL = '' } = author || {};
  const form = useFormContext<FormSchema>();

  return (
    <>
      <PostAuthorRow name={name} surname={surname} avatarURL={avatarURL} audience={audience} onOpenAudience={onOpenAudience} selectedGroups={selectedGroups} />
      <FormField
        name={'name'}
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tytuł</FormLabel>
            <FormControl>
              <Input {...field} placeholder={'Tytuł'} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name={'content'}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Treść</FormLabel>
            <div className="border-border-primary space-y-2 rounded-md border">
              <FormControl>
                <Tiptap
                  {...field}
                  placeholder={'Co chcesz przekazać ?'}
                  extensions={postExtensionSet}
                  onUpdate={(res) => {
                    if (res.editor.isEmpty) {
                      field.onChange('');
                      return;
                    }
                    field.onChange(res.editor.getHTML());
                  }}
                  content={field.value ?? ''}
                  className="p-1"
                >
                  <TiptapToolbar className={'-top-4 rounded-t-sm p-3'} />
                </Tiptap>
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className={'flex flex-col gap-2'}>
        <Label>Media</Label>
        <FileInput onAddFiles={onAddFiles}>
          {files.map(({ id, file }) => (
            <FileInputCard key={id} file={file} onRemove={() => onRemoveFile(id)} />
          ))}
          {currentFiles.map((file) => (
            <FileInputCard key={file.id} file={file} onRemove={() => onRemoveFile(file.id)} />
          ))}
        </FileInput>
      </div>
    </>
  );
}
